/**
 * Chart palette for the meta analytics pages.
 *
 * These eight hues are a validated categorical set: assigned in this fixed slot
 * order they clear colourblind-separation, normal-vision separation and contrast
 * gates on both light and dark surfaces. Two rules keep that guarantee true:
 *
 *   1. Assign slots in order and NEVER cycle. A ninth series folds into "Other"
 *      rather than repeating or generating a hue.
 *   2. Colour follows the entity, not its rank — `buildColorScale` pins a colour
 *      to an archetype label, so filtering the chart never repaints the survivors.
 *
 * Three light-mode hues (magenta, yellow, aqua) sit below 3:1 on a white surface,
 * so every chart using them also ships direct labels or the table view beneath it.
 */

export const CATEGORICAL_LIGHT = [
  '#2a78d6', // 1 blue
  '#eb6834', // 2 orange
  '#1baf7a', // 3 aqua
  '#eda100', // 4 yellow
  '#e87ba4', // 5 magenta
  '#008300', // 6 green
  '#4a3aa7', // 7 violet
  '#e34948', // 8 red
];

export const CATEGORICAL_DARK = [
  '#3987e5', '#d95926', '#199e70', '#c98500',
  '#d55181', '#008300', '#9085e9', '#e66767',
];

/** Neutral used for the folded "Other" bucket — never a categorical slot. */
export const OTHER_COLOR_LIGHT = '#9a9a94';
export const OTHER_COLOR_DARK = '#6f6f6a';

export const OTHER_LABEL = 'Other';

/** Max distinct series before the rest folds into "Other". */
export const MAX_SERIES = CATEGORICAL_LIGHT.length;

/**
 * Pin a colour to each label, in slot order, so a label keeps its colour
 * regardless of how the chart is later filtered or re-sorted.
 *
 * @param {string[]} labels - series labels, in the order slots should be assigned
 * @param {boolean} isDark
 * @returns {(label: string) => string}
 */
export function buildColorScale(labels, isDark = false) {
  const ramp = isDark ? CATEGORICAL_DARK : CATEGORICAL_LIGHT;
  const other = isDark ? OTHER_COLOR_DARK : OTHER_COLOR_LIGHT;
  const assigned = new Map();
  let slot = 0;
  for (const label of labels) {
    if (label === OTHER_LABEL || assigned.has(label)) continue;
    if (slot < ramp.length) assigned.set(label, ramp[slot++]);
  }
  return (label) => assigned.get(label) || other;
}

/** Ink and grid colours, matching the app's CSS variables. */
export const CHART_INK = {
  light: { text: '#0f172a', muted: '#64748b', grid: 'rgba(15, 23, 42, 0.08)', surface: '#ffffff' },
  dark: { text: '#f8fafc', muted: '#94a3b8', grid: 'rgba(248, 250, 252, 0.12)', surface: '#0b1220' },
};

/**
 * Hit-testing for a HORIZONTAL bar chart (indexAxis: 'y').
 *
 * Chart.js measures along x by default, which on a horizontal bar chart is the
 * *value* axis — so pointing at a short bar could surface a longer neighbour's
 * tooltip. The categories run along y, so that is the axis to hit-test on.
 */
export const HORIZONTAL_BAR_INTERACTION = { mode: 'index', axis: 'y', intersect: false };

/**
 * Shared Chart.js options. Grid and axes stay recessive; the tooltip is on by
 * default because an on-screen chart is an interactive one.
 */
export function baseChartOptions({ isDark = false, percentAxis = false, stacked = false } = {}) {
  const ink = isDark ? CHART_INK.dark : CHART_INK.light;
  return {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          boxWidth: 10,
          boxHeight: 10,
          padding: 12,
          usePointStyle: true,
          color: ink.muted,
          font: { size: 11 },
        },
      },
      tooltip: {
        backgroundColor: isDark ? '#1e293b' : '#0f172a',
        titleFont: { size: 12 },
        bodyFont: { size: 12 },
        padding: 10,
        callbacks: percentAxis
          ? {
              label: (ctx) => `${ctx.dataset.label}: ${(ctx.parsed.y * 100).toFixed(1)}%`,
            }
          : undefined,
      },
    },
    scales: {
      x: {
        stacked,
        grid: { display: false },
        ticks: { color: ink.muted, font: { size: 11 } },
      },
      y: {
        stacked,
        beginAtZero: true,
        grid: { color: ink.grid, drawBorder: false },
        ticks: {
          color: ink.muted,
          font: { size: 11 },
          callback: percentAxis
            ? (value) => `${Math.round(value * 100)}%`
            : undefined,
        },
      },
    },
  };
}
